import {
  formattedDateForSubscription,
  QUOTE_CANCEL_REQUEST,
  QuoteStatusEnum,
  type ErrorSubscriptionResponse,
  type IWebSocketClient,
  type Quote,
} from '@talos/kyoko';
import type { Observable } from 'rxjs';

const CANCEL_QUOTE_TIMEOUT_MS = 5_000;

export interface CancelQuoteDeps {
  webSocketClient: IWebSocketClient<unknown>;
  quoteSub: Observable<{ data: Quote[] }>;
}

/**
 * Publishes a QuoteCancelRequest and resolves once the quote stream reports the
 * matching QuoteReqID as canceled.
 */
export async function cancelQuote(quoteReqID: string, deps: CancelQuoteDeps): Promise<Quote> {
  const { webSocketClient, quoteSub } = deps;
  const cleanups: (() => void)[] = [];

  // Resolve only from quote feedback. Publication success means the request was sent,
  // not that the quote is actually canceled.
  const quoteFeedbackPromise = new Promise<Quote>(resolve => {
    const subscription = quoteSub.subscribe(response => {
      const quote = response.data.findLast(quote => quote.QuoteReqID === quoteReqID);
      if (quote?.QuoteStatus === QuoteStatusEnum.Canceled) {
        resolve(quote);
      }
    });

    cleanups.push(() => subscription.unsubscribe());
  });

  const publicationErrorPromise = new Promise<never>((_, reject) => {
    const cleanupPublication = webSocketClient.registerPublication(
      {
        type: QUOTE_CANCEL_REQUEST,
        data: [
          {
            QuoteReqID: quoteReqID,
            TransactTime: formattedDateForSubscription(new Date()),
          },
        ],
      },
      (err: ErrorSubscriptionResponse) => {
        reject(new Error(err.error?.msg ?? 'Failed to cancel quote'));
      }
    );

    cleanups.push(cleanupPublication);
  });

  const timeoutPromise = new Promise<never>((_, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Timed out waiting for quote cancellation.'));
    }, CANCEL_QUOTE_TIMEOUT_MS);

    cleanups.push(() => clearTimeout(timeoutId));
  });

  return Promise.race([quoteFeedbackPromise, publicationErrorPromise, timeoutPromise]).finally(() => {
    cleanups.forEach(cleanup => cleanup());
  });
}
